import { useEffect, useMemo, useState } from 'react'

import { useForm, FormProvider } from 'react-hook-form'

import { useDigitalProductContext } from '@/shared/contexts/DigitalProductContext'
import { useAppRouter } from '@/shared/hooks/useAppRouter'
import { useUserProgress } from '@/shared/hooks/useUserProgress'
import { api } from '@/shared/lib/api'
import { getErrorMessage } from '@/shared/lib/errors'
import { Button, Modal } from '@/shared/ui'
import { AppIcon  } from '@/shared/ui/icons'
import { AppMenuScrollArea } from '@/shared/ui/Layout/menu/AppMenuScrollArea'

// A API devolve a falha de validação sob o nome genérico do atributo, não o
// `id` do addInfo que falhou — não há como apontar o erro a um campo. Esta
// tabela só troca o rótulo técnico por um texto que o participante entende.
const ADD_INFO_ERROR_KEY_NAMES = {
  additional_informations: { _name: 'Informações adicionais' }
}

import { DigitalProductCheckoutAditionalInformationFields } from './AdditionalInformation'
import {
  findTicketWithUnansweredRequiredAddInfos,
  getAddInfoAnswerRecord,
  getAddInfoAnswerValue,
  getAddInfosByPriceId,
  isDigitalProductHomePath,
  isRequiredAddInfoUnanswered,
  mergeTicketAdditionalInformations,
  ticketHasUnansweredRequiredAddInfos
} from './utils'

const AddInfoSurveyModal = ({
  digitalProduct,
  id,
  price_id: priceId,
  additional_informations: addInfoAnswers = [],
  userTickets,
  onSave
}) => {
  const pendingAddInfos = getAddInfosByPriceId({
    digitalProduct,
    priceId,
    onlyRequired: true
  }).filter(addInfo =>
    isRequiredAddInfoUnanswered(
      addInfo,
      { id, price_id: priceId, additional_informations: addInfoAnswers },
      userTickets
    )
  )

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState(null)
  const methods = useForm()

  const handleSaveAddInfo = async data => {
    if (isSubmitting) return

    setIsSubmitting(true)
    setSubmitError(null)

    try {
      const merged = mergeTicketAdditionalInformations(
        addInfoAnswers,
        data?.additional_informations
      )

      await api.put(`/user/tickets/${id}`, {
        record: {
          additional_informations: merged
        }
      })

      onSave({
        ticketId: id,
        additional_informations: merged
      })
    } catch (error) {
      // Erro de formulário, não de campo: a API não nomeia qual resposta
      // falhou. Os valores preenchidos ficam como estão; não há `reset`.
      const { message } = getErrorMessage(error, ADD_INFO_ERROR_KEY_NAMES)
      setSubmitError(message)
    } finally {
      setIsSubmitting(false)
    }
  }

  if (!pendingAddInfos.length) return null

  return (
    <Modal show centered>
      <Modal.Header>
        <Modal.Icon name="block-question" size={56} className="text-primary" />
        <Modal.Title>
          Antes de continuar, por favor responda o questionário abaixo
        </Modal.Title>
      </Modal.Header>

      <FormProvider {...methods}>
        <form
          onSubmit={methods.handleSubmit(handleSaveAddInfo)}
          // `min-h-0` permite este item de flex encolher abaixo do conteúdo:
          // sem ele o corpo não tem altura finita contra a qual rolar, e cresce
          // até o `max-h` do `DialogContent` cortar o excedente.
          className="flex min-h-0 flex-1 flex-col w-full"
        >
          {/* Área rolável própria em vez do `Modal.Body`: desenha trilho e
              polegar a partir de `--color-brand`, então a barra acompanha a cor
              do evento em vez de herdar a do sistema, que no mobile só aparece
              durante o gesto. */}
          <AppMenuScrollArea
            label="Campos do questionário"
            hintLabel="Role para ver mais campos do questionário"
            // Sem esticar: com poucos campos, preencher o espaço do pai deixa
            // um vão entre o último campo e o rodapé.
            grow={false}
          >
            {/* `pb-4`: o rodapé logo abaixo já traz o respiro contra a borda
                do diálogo, e os dois somados viram vão branco. */}
            <div className="px-10 pb-4 lg:px-[3.75rem] [&>*:last-child]:mb-0">
              {pendingAddInfos.map((addInfo, addInfoIndex) => {
                const addInfoAnswer = getAddInfoAnswerRecord(
                  addInfoAnswers,
                  addInfo.id,
                  addInfo.name
                )
                const answer = getAddInfoAnswerValue(addInfoAnswer, addInfo)

                return (
                  <DigitalProductCheckoutAditionalInformationFields
                    key={addInfo?.id}
                    namePrefix={`additional_informations.${addInfoIndex}`}
                    answer={answer}
                    {...addInfo}
                  />
                )
              })}
            </div>
          </AppMenuScrollArea>
          {submitError && (
            <p role="alert" className="px-10 lg:px-[3.75rem] pb-4 text-sm text-danger-text">
              {submitError}
            </p>
          )}
          <Modal.Footer>
            <div className="flex items-center justify-center">
              <Button
                type="submit"
                variant="success"
                disabled={isSubmitting}
                loading={isSubmitting}
                showTrailingIcon={false}
                leadingIcon={
                  !isSubmitting ? <AppIcon name="check-double" /> : undefined
                }
              >
                {isSubmitting ? 'Respondendo...' : 'Responder e prosseguir'}
              </Button>
            </div>
          </Modal.Footer>
        </form>
      </FormProvider>
    </Modal>
  )
}

export const AddInfoSurvey = () => {
  const { digitalProduct, basePathWithId } = useDigitalProductContext()
  const { pathname } = useAppRouter()
  const { userProgress, refetchProgress } = useUserProgress()
  const [ticketAnswersOverride, setTicketAnswersOverride] = useState({})
  const [dismissedTicketIds, setDismissedTicketIds] = useState(() => new Set())

  const isHomePath = isDigitalProductHomePath(pathname, basePathWithId)

  const tickets = useMemo(() => {
    const base = userProgress?.user_tickets ?? []
    if (!Object.keys(ticketAnswersOverride).length) return base

    return base.map(ticket =>
      ticketAnswersOverride[ticket.id]
        ? {
            ...ticket,
            additional_informations: ticketAnswersOverride[ticket.id]
          }
        : ticket
    )
  }, [ticketAnswersOverride, userProgress?.user_tickets])

  useEffect(() => {
    if (!digitalProduct || !userProgress?.user_tickets?.length) return

    setDismissedTicketIds(prev => {
      let changed = false
      const next = new Set(prev)

      for (const ticketId of prev) {
        const ticket = userProgress.user_tickets.find(t => t.id === ticketId)
        if (
          ticket &&
          ticketHasUnansweredRequiredAddInfos({
            digitalProduct,
            ticket,
            tickets: userProgress.user_tickets
          })
        ) {
          next.delete(ticketId)
          changed = true
        }
      }

      return changed ? next : prev
    })
  }, [digitalProduct, userProgress?.user_tickets])

  const unansweredTicket = useMemo(() => {
    if (!isHomePath || !userProgress || !digitalProduct) return null

    const candidate = findTicketWithUnansweredRequiredAddInfos({
      digitalProduct,
      tickets
    })

    if (!candidate || dismissedTicketIds.has(candidate.id)) return null

    return candidate
  }, [digitalProduct, dismissedTicketIds, isHomePath, tickets, userProgress])

  const handleSave = ({ ticketId, additional_informations }) => {
    const baseTickets = userProgress?.user_tickets ?? []
    const ticketsAfterSave = baseTickets.map(ticket =>
      ticket.id === ticketId
        ? { ...ticket, additional_informations }
        : ticket
    )

    setTicketAnswersOverride(prev => ({
      ...prev,
      [ticketId]: additional_informations
    }))

    const ticket = ticketsAfterSave.find(t => t.id === ticketId)
    const stillUnanswered =
      ticket &&
      ticketHasUnansweredRequiredAddInfos({
        digitalProduct,
        ticket,
        tickets: ticketsAfterSave
      })

    if (!stillUnanswered) {
      setDismissedTicketIds(prev => new Set([...prev, ticketId]))
    }

    refetchProgress()
  }

  if (!unansweredTicket) return null

  return (
    <AddInfoSurveyModal
      onSave={handleSave}
      digitalProduct={digitalProduct}
      userTickets={tickets}
      {...unansweredTicket}
    />
  )
}
